
import { CheckCircle, AlertTriangle, Clock, RefreshCw, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Purchase } from "@/lib/purchases";

interface StatusDisplayProps {
  purchase: Purchase | null;
  loading: boolean;
  refreshing: boolean;
  onRefresh: () => void;
  autoRefreshActive: boolean;
  courseId: string | null;
}

const StatusDisplay = ({ 
  purchase, 
  loading, 
  refreshing, 
  onRefresh, 
  autoRefreshActive,
  courseId 
}: StatusDisplayProps) => {
  // Default success status
  let statusTitle = "Tellimus edukalt esitatud!";
  let StatusIcon = CheckCircle;
  let statusColor = "bg-green-100";
  let iconColor = "text-green-600";
  let message =
    "Sinu ost on edukalt lõpetatud. Kohe saad alustada oma õpiteekonda.";
  let redirectMessage =
    "Teid suunatakse automaatselt kursusele 10 sekundi pärast...";
  let extraMessage = null;
  let showRefreshButton = false;

  if (loading) {
    message = "Kontrollime makse staatust...";
    redirectMessage = null;
  } else if (purchase && (purchase.payment_status === "cancelled" || purchase.status === "cancelled")) {
    // New state for cancelled payments
    statusTitle = "Makse tühistati";
    StatusIcon = XCircle;
    statusColor = "bg-amber-100";
    iconColor = "text-amber-600";
    message =
      "Makse tühistati või jäeti pooleli. Sa ei ole selle kursuse eest veel tasunud.";
    redirectMessage = null;
    extraMessage = (
      <div className="mt-4 space-y-4">
        <Button asChild className="bg-kaija-accent hover:bg-opacity-90 text-white px-8 py-6 text-lg">
          <Link to={courseId ? `/checkout?courseId=${courseId}` : "/courses"}>
            Proovi maksta uuesti
          </Link>
        </Button>
      </div>
    );
    showRefreshButton = false;
  } else if (purchase && (purchase.payment_status === "failed" || purchase.status === "failed")) {
    statusTitle = "Makse ebaõnnestus";
    StatusIcon = AlertTriangle;
    statusColor = "bg-red-100";
    iconColor = "text-red-600";
    message =
      "Makse ei õnnestunud. Sa ei ole selle kursuse eest veel tasunud.";
    redirectMessage =
      "Palun proovi uuesti või võta ühendust toega, kui probleem püsib.";
    extraMessage = (
      <div className="mt-4">
        <Button asChild className="bg-kaija-accent hover:bg-opacity-90 text-white px-8 py-6 text-lg">
          <Link to={courseId ? `/checkout?courseId=${courseId}` : "/courses"}>
            Proovi maksta uuesti
          </Link>
        </Button>
      </div>
    );
    showRefreshButton = false;
  } else if (purchase && purchase.payment_status === "timeout") {
    // New state for timed out status checks
    statusTitle = "Makse staatuse kontrollimine aegus";
    StatusIcon = Clock;
    statusColor = "bg-yellow-100";
    iconColor = "text-yellow-600";
    message =
      "Makse staatuse kontroll võttis liiga kaua aega. Palun värskenda staatust käsitsi või proovi uuesti maksta.";
    redirectMessage = null;
    extraMessage = (
      <div className="mt-4 space-y-4">
        <Button 
          onClick={onRefresh}
          disabled={refreshing}
          className="bg-kaija-accent hover:bg-opacity-90 text-white px-8 py-6 text-lg"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Värskendamine..." : "Värskenda staatust"}
        </Button>
        
        <div className="mt-4">
          <Button asChild variant="outline" className="mt-2">
            <Link to={courseId ? `/checkout?courseId=${courseId}` : "/courses"}>
              Proovi uuesti maksta
            </Link>
          </Button>
        </div>
      </div>
    );
    showRefreshButton = true;
  } else if (
    purchase &&
    purchase.payment_status !== "completed" &&
    purchase.status !== "completed"
  ) {
    // Pending, processing or unknown state
    statusTitle = "Makse olek: töötlemisel";
    StatusIcon = Clock;
    statusColor = "bg-yellow-100";
    iconColor = "text-yellow-600";
    message =
      "Makse tulemus on veel laekumata või on makse pooleli. Palun oota paar hetke ja värskenda staatust, või küsi abi, kui probleem püsib.";
    redirectMessage = null;
    showRefreshButton = true;
    
    extraMessage = (
      <div className="mt-4 space-y-4">
        <Button 
          onClick={onRefresh}
          disabled={refreshing}
          className="bg-kaija-accent hover:bg-opacity-90 text-white px-8 py-6 text-lg"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Värskendamine..." : "Värskenda staatust"}
        </Button>
        
        <div className="mt-4">
          <Button asChild variant="outline" className="mt-2">
            <Link to={courseId ? `/checkout?courseId=${courseId}` : "/courses"}>
              Proovi uuesti maksta
            </Link>
          </Button>
        </div>
        
        {autoRefreshActive && (
          <div className="text-sm text-gray-500 mt-2">
            <span className="animate-pulse inline-block mr-2">●</span>
            Automaatne värskendamine aktiivne...
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="text-center mb-8">
      <div className={`mx-auto w-16 h-16 mb-4 ${statusColor} rounded-full flex items-center justify-center`}>
        <StatusIcon className={`h-8 w-8 ${iconColor}`} />
      </div>
      <h2 className="text-2xl md:text-3xl font-medium mb-3">{statusTitle}</h2>
      <p className="text-gray-700 mb-6">{message}</p>
      {redirectMessage && <p className="text-sm text-kaija-accent mb-4">{redirectMessage}</p>}
      {extraMessage}
    </div>
  );
};

export default StatusDisplay;
